import React, { useEffect, useRef, useMemo, useState } from 'react';

const loadTwitchScript = (callback) => {
    if (window.Twitch && window.Twitch.Embed) {
        callback();
        return;
    }
    const existingScript = document.querySelector('script[src="https://embed.twitch.tv/embed/v1.js"]');
    if (existingScript) {
        existingScript.addEventListener('load', callback);
        return;
    }
    const script = document.createElement('script');
    script.setAttribute('src', 'https://embed.twitch.tv/embed/v1.js');
    script.addEventListener('load', callback);
    document.body.appendChild(script);
};

const StreamEmbed = ({ link, muted = true, volume = 0, autoplay = false }) => {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const twitchPlayer = useRef(null);
  const [twitchReady, setTwitchReady] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const hostname = window.location.hostname;
  const cleanLink = link ? link.trim() : "";

  // 1. DETERMINE TYPE (Memoized to avoid recalc)
  const streamType = useMemo(() => {
      if (!cleanLink) return null;
      if (cleanLink.match(/twitch\.tv\/([a-zA-Z0-9_]+)/)) return 'twitch';
      if (cleanLink.match(/(?:youtu\.be\/|youtube\.com\/)/)) return 'youtube';
      return null;
  }, [cleanLink]);

  // 2. YOUTUBE URL GENERATOR (Memoized)
  // We EXCLUDE muted/volume from deps so URL doesn't change on volume slide
  const youtubeUrl = useMemo(() => {
      if (streamType !== 'youtube') return null;
      const ytMatch = cleanLink.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|live\/|watch\?v=|watch\?.+&v=))([^#&?]*)/);
      if (ytMatch && ytMatch[1]) {
        const videoId = ytMatch[1];
        const params = new URLSearchParams();
        params.set('autoplay', autoplay ? '1' : '0');
        params.set('mute', '0'); // Start muted, update via command
        params.set('loop', '0');
        params.set('enablejsapi', '1'); 
        params.set('rel', '0');
        params.set('modestbranding', '1');
        return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
      }
      return null;
  }, [cleanLink, streamType, autoplay]);


  // 3. INITIALIZE TWITCH
  useEffect(() => {
      if (streamType !== 'twitch') return;
      
      const twitchMatch = cleanLink.match(/twitch\.tv\/([a-zA-Z0-9_]+)/);
      const channel = twitchMatch ? twitchMatch[1] : null;
      if (!channel) return;

      if (containerRef.current) {
          containerRef.current.innerHTML = ''; 
      }

      loadTwitchScript(() => {
          if (!containerRef.current) return;
          const options = {
              width: '100%',
              height: '100%',
              channel: channel,
              parent: [hostname, 'localhost'],
              autoplay: autoplay,
              muted: true,
              controls: true
          };
          const player = new window.Twitch.Embed(containerRef.current, options);
          
          player.addEventListener(window.Twitch.Embed.VIDEO_READY, () => {
              twitchPlayer.current = player;
              setTwitchReady(true);
          });
      });

      return () => {
          twitchPlayer.current = null;
      };
  }, [streamType, cleanLink, hostname, autoplay]);


  // 4. HANDLE VOLUME/MUTE UPDATES (Side Effect Only)
  useEffect(() => {
      // YouTube
      if (streamType === 'youtube' && iframeRef.current) {
          const win = iframeRef.current.contentWindow;
          if (win) {
              win.postMessage(JSON.stringify({ event: 'command', func: 'setVolume', args: [volume] }), '*');
              win.postMessage(JSON.stringify({ event: 'command', func: muted ? 'mute' : 'unMute', args: [] }), '*');
          }
      }

      // Twitch
      if (streamType === 'twitch' && twitchPlayer.current && twitchReady) {
          const player = twitchPlayer.current.getPlayer();
          if (player) {
              player.setVolume(volume / 100); 
              player.setMuted(muted);
          }
      }
  }, [volume, muted, streamType, twitchReady]);

  useEffect(() => {
        if (streamType !== 'youtube') return;

        const iframe = iframeRef.current;
        
        const setupYoutubeListener = () => {
            if (iframe && iframe.contentWindow) {
                // Tell YouTube to start broadcasting its state changes to our window
                iframe.contentWindow.postMessage(JSON.stringify({ event: 'listening' }), '*');
            }
        };

        const handleMessage = (e) => {
            // Security: Only trust messages from YouTube
            if (e.origin !== "https://www.youtube.com") return;
            
            try {
                const data = JSON.parse(e.data);
                
                // playerState 0 = "Ended"
                if (data.event === 'infoDelivery' && data.info && data.info.playerState === 0) {
                    // Force React to destroy and recreate the iframe, restoring the thumbnail
                    setReloadKey(prev => prev + 1);
                }
            } catch (err) {
                // Ignore random non-JSON messages from browser extensions, etc.
            }
        };

        window.addEventListener('message', handleMessage);
        
        if (iframe) {
            iframe.addEventListener('load', setupYoutubeListener);
        }

        return () => {
            window.removeEventListener('message', handleMessage);
            if (iframe) iframe.removeEventListener('load', setupYoutubeListener);
        };
    }, [streamType]);

  // 5. RENDER
  if (!cleanLink) return null;

  // --- ERROR HANDLING FOR CHANNEL LINKS ---
  // If the user pasted a channel link (e.g. @Handle/live), show a helpful error
  if (cleanLink.includes('/@') || cleanLink.includes('/channel/') || cleanLink.includes('/c/')) {
      return (
        <div style={{
            width: '100%', height: '100%', 
            display: 'flex', flexDirection: 'column', alignItems:'center', justifyContent:'center', 
            backgroundColor: '#111', color: '#888', fontSize: 13, padding: 20, textAlign: 'center'
        }}>
            {/* <div style={{marginBottom: 8, fontSize: 16}}>⚠️</div> */}
            <div><b>This Bridge Brawl stream is not yet live!</b></div>
            <a 
                href={cleanLink} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{textDecoration: 'none', color: 'inherit', cursor: 'pointer', display: 'block'}}
            >
                <div style={{marginTop: 5, fontSize: 12, opacity: 0.8}}>
                    You can visit this creator's page at <b style={{color: '#6C5DD3', textDecoration: 'underline'}}>{cleanLink}</b>
                </div>
            </a>
        </div>
      );
  }

  // YouTube
  if (streamType === 'youtube' && youtubeUrl) {
      return (
        <iframe 
            key={`yt-embed-${reloadKey}`} // <--- THIS FORCES THE RELOAD
            ref={iframeRef}
            src={youtubeUrl}
            width="100%" height="100%" 
            allowFullScreen title="YouTube Stream"
            style={{border: 0}}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        ></iframe>
      );
  }

  // Twitch
  if (streamType === 'twitch') {
      return <div ref={containerRef} style={{width: '100%', height: '100%', border: 0}} id={`twitch-${Date.now()}`}></div>;
  }

  // Fallback
  return (
    <div style={{width: '100%', height: '100%', display: 'flex', alignItems:'center', justifyContent:'center', backgroundColor: '#000', color: '#555', fontSize: 12}}>
        Invalid Stream URL
    </div>
  );
};

export { StreamEmbed };
